import React, { useState } from "react";
import { X, Mail, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const KINDS = [
  { kind: "early", title: "Early-access email", desc: "What Pack members get the day before launch — includes their private 24h-early link." },
  { kind: "launch", title: "Launch-day email", desc: "What every newsletter subscriber gets when the collection switches on." },
  { kind: "countdown", title: "Your countdown card email", desc: "The 'drops in 7 days' teaser that lands in your sales inbox a week before." },
  { kind: "owner", title: "Your launch card email", desc: "The ready-to-post launch card you get the morning it opens." },
  { kind: "thanks", title: "Early-access thank-you", desc: "The instant 'you're on the list' email with a sneak peek, sent when someone taps Get early access." },
  { kind: "wrap", title: "Season wrap report", desc: "Your sales, best sellers and early-access recap, sent the morning after the season closes." },
];

export const SeasonPreviewModal = ({ season, defaultEmail, onClose, onLinkSaved }) => {
  const [to, setTo] = useState(defaultEmail || "");
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState(season.peek_url || "");
  const saveLink = async () => {
    try {
      const { data } = await api.put(`/admin/seasonal-collections/${season.slug}/link`, { peek_url: link.trim() });
      toast.success(data.peek_url ? "Link saved — emails and captions now use it" : "Link cleared — back to the default share link");
      onLinkSaved?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const send = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/admin/seasonal-collections/${season.slug}/preview-email?kind=${kind}&to=${encodeURIComponent(to)}`);
      data.ok ? toast.success(data.message) : toast.error(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy("");
  };
  const [early, setEarly] = useState(null);
  const copyEarly = async () => {
    try {
      const { data } = early ? { data: early } : await api.get(`/admin/seasons/${season.slug}/early-link`);
      setEarly(data);
      await navigator.clipboard.writeText(data.link);
      toast.success(data.window_open ? "Secret early-access link copied — it works right now" : `Copied — it unlocks on ${new Date(`${data.unlocks_from}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-[#19324A]/60 flex items-center justify-center p-4" onClick={onClose} data-testid="season-preview-modal">
      <div className="bg-white rounded-3xl p-6 w-full max-w-md soft-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-extrabold text-xl text-[#19324A]">{season.emoji} {season.label} — send me a preview</h3>
            <p className="text-sm text-[#19324A]/60 mt-1">See exactly what lands in inboxes before the season opens.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#19324A]/5" data-testid="season-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-5 rounded-2xl border-2 border-dashed border-[#7ED957]/60 bg-[#7ED957]/10 p-3" data-testid="season-early-link-box">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0"><p className="text-sm font-bold text-[#166534]">Secret early-access link</p><p className="text-xs text-[#19324A]/60">Unlocks the collection 24h before it opens — this is what subscribers get.</p></div>
            <button onClick={copyEarly} className="btn-outline !px-3 !py-2 text-xs shrink-0" data-testid="season-early-link-copy"><Link2 className="w-4 h-4" /> Copy link</button>
          </div>
          {early && <p className="mt-2 text-[11px] font-mono break-all text-[#19324A]/70 select-all" data-testid="season-early-link-url">{early.link}</p>}
        </div>
        <label className="block text-xs font-semibold text-[#19324A]/60 mt-5 mb-1">Sneak-peek / collection link used in captions &amp; emails</label>
        <div className="flex gap-2">
          <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder={`https://thehappybulldogco.com.au/${season.slug}`} className="flex-1 min-w-0 rounded-xl border-2 border-[#19324A]/10 px-3 py-2 text-sm outline-none focus:border-[#FFD93D]" data-testid="season-link-input" />
          <button onClick={saveLink} className="btn-outline !px-3 !py-2 text-xs shrink-0" data-testid="season-link-save">Save</button>
        </div>
        <p className="text-[11px] text-[#19324A]/50 mt-1">Leave blank to use the default share link (with the Facebook preview card). Pretty links like /{season.slug} on your site redirect to the collection.</p>
        <label className="block text-xs font-semibold text-[#19324A]/60 mt-5 mb-1">Send previews to</label>
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)} className="w-full rounded-xl border-2 border-[#19324A]/10 px-3 py-2 text-sm outline-none focus:border-[#FFD93D]" data-testid="season-preview-email" />
        <ul className="mt-4 space-y-2">
          {KINDS.map((k) => (
            <li key={k.kind} className="flex items-center justify-between gap-3 rounded-2xl bg-[#FFF9EE] px-4 py-3">
              <div className="min-w-0"><p className="text-sm font-bold text-[#19324A]">{k.title}</p><p className="text-xs text-[#19324A]/60">{k.desc}</p></div>
              <button onClick={() => send(k.kind)} disabled={!!busy || !to} className="btn-outline !px-3 !py-2 text-xs shrink-0 disabled:opacity-50" data-testid={`season-preview-send-${k.kind}`}>
                {busy === k.kind ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
